import { Action } from "redux";
import { ISessionState } from "../session/sessionState";
import { IInstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";
import { IWhistleBlowState } from "./whistleBlow/storeWhistleBlow";
import { IRecordState } from "./record/storeRecord";
import { IEventState } from "./event/storeEvent";
import { ITaskState } from "./task/storeTask";
import { IDocumentState } from "./document/storeDocument";
import { ICompanyState } from "./company/storeCompany";
import { ICompanyGroupState } from "./companyGroup/storeCompanyGroup";
import { IUserState } from "./user/storeUser";
import { IUserGroupState } from "./userGroup/storeUserGroup";
import { ILogState } from "./log/storeLog";
import { IInstantMessageState } from "./instantMessage/storeInstantMessage";
import { IMessageState } from "./message/storeMessage";
import { ICalendarTaskState } from "./calendarTask/storeCalendarTask";
import { IFrontState } from "./front/storeFront";
import { IAppReportState } from "./appReport/storeAppReport";
import { IRiskState } from "./risk/storeRisk";

export interface ILoadingState {
    users: boolean;
}

/* eslint-disable no-unused-vars */
export enum ConfirmationDialogType {
    Warning,
    Information
}
/* eslint-enable no-unused-vars */

export interface IConfirmationCallback {
    (): void;
}

export interface IAlertState {
    message: string;
    info: string;
    error: string;
}

export interface IConfirmationState {
    type: ConfirmationDialogType;
    title: string;
    text: string;
    show: boolean;
    yesConfirmationText: string;
    onYes: IConfirmationCallback;
    onNo: IConfirmationCallback;
    onCancel: IConfirmationCallback;
}

export interface IAutoLogoutState {
    show: boolean;
}

export interface IAppSessionState {
    state: ISessionState;
    newInstantMessages: IInstantMessageListItem[];
}

export interface IApplicationState {
    // Fetch
    fetchCount: number;
    // Alert
    alert: IAlertState;
    // Confirmation
    confirmation: IConfirmationState;
    // AutoLogout
    autoLogout: IAutoLogoutState;
    // Session
    session: IAppSessionState;
    // Front
    front: IFrontState;
    // Document
    document: IDocumentState;
    // Event
    event: IEventState;
    // Record
    record: IRecordState;
    // Company
    company: ICompanyState;
    // CompanyCroup
    companyGroup: ICompanyGroupState;
    // Message
    message: IMessageState;
    // Task
    task: ITaskState;
    // User
    user: IUserState;
    // UserGroup
    userGroup: IUserGroupState;
    // Log
    log: ILogState;
    // Instant Message
    instantMessage: IInstantMessageState;
    // WhistleBlow
    whistleBlow: IWhistleBlowState
    // CalendarTask
    calendarTask: ICalendarTaskState;
    // AppReport
    appReport: IAppReportState;
    // Risk
    risk: IRiskState;
}

// Action type constants
// ***********************************************************************

// Alert
export const ALERT_SUCCESS = "ALERT_SUCCESS";
export const ALERT_INFO = "ALERT_INFO";
export const ALERT_ERROR = "ALERT_ERROR";
export const ALERT_CLEAR = "ALERT_CLEAR";
export const ALERT_SUCCESS_CLEAR = "ALERT_SUCCESS_CLEAR";
export const ALERT_INFO_CLEAR = "ALERT_INFO_CLEAR";

type T_ALERT_SUCCESS = typeof ALERT_SUCCESS;
type T_ALERT_INFO = typeof ALERT_INFO;
type T_ALERT_ERROR = typeof ALERT_ERROR;
type T_ALERT_CLEAR = typeof ALERT_CLEAR;
type T_ALERT_SUCCESS_CLEAR = typeof ALERT_SUCCESS_CLEAR;
type T_ALERT_INFO_CLEAR = typeof ALERT_INFO_CLEAR;

// Confirmation
export const CONFIRMATION_SET = "CONFIRMATION_SET";
export const CONFIRMATION_CLEAR = "CONFIRMATION_CLEAR";

type T_CONFIRMATION_SET = typeof CONFIRMATION_SET;
type T_CONFIRMATION_CLEAR = typeof CONFIRMATION_CLEAR;

// AutoLogout
export const AUTOLOGOUT_SET = "AUTOLOGOUT_SET";
export const AUTOLOGOUT_CLEAR = "AUTOLOGOUT_CLEAR";

type T_AUTOLOGOUT_SET = typeof AUTOLOGOUT_SET;
type T_AUTOLOGOUT_CLEAR = typeof AUTOLOGOUT_CLEAR;

// Fetch
export const FETCH_START = "FETCH_START";
export const FETCH_END = "FETCH_END";

type T_FETCH_START = typeof FETCH_START;
type T_FETCH_END = typeof FETCH_END;

// Session
export const SESSION_SET_STATE = "SESSION_SET_STATE";
export const NEW_INSTANT_MESSAGES_SET = "NEW_INSTANT_MESSAGES_SET";

type T_SESSION_SET_STATE = typeof SESSION_SET_STATE;
type T_NEW_INSTANT_MESSAGES_SET = typeof NEW_INSTANT_MESSAGES_SET;

// Action types
// ***********************************************************************

// Alert
export interface IAlertSuccess extends Action {
    type: T_ALERT_SUCCESS;
    message: string;
}

export interface IAlertInfo extends Action {
    type: T_ALERT_INFO;
    message: string;
}

export interface IAlertError extends Action {
    type: T_ALERT_ERROR;
    message: string;
}

export interface IAlertClear extends Action {
    type: T_ALERT_CLEAR;
}

export interface IAlertSuccessClear extends Action {
    type: T_ALERT_SUCCESS_CLEAR;
}

export interface IAlertInfoClear extends Action {
    type: T_ALERT_INFO_CLEAR;
}

export type AlertAction =
    | IAlertSuccess
    | IAlertInfo
    | IAlertError
    | IAlertClear
    | IAlertSuccessClear
    | IAlertInfoClear;

// Confirmation
export interface IConfirmationSet extends Action {
    type: T_CONFIRMATION_SET;
    dialogType: ConfirmationDialogType;
    title: string;
    text: string;
    show: boolean;
    yesConfirmationText: string;
    onYes: IConfirmationCallback;
    onNo: IConfirmationCallback;
    onCancel: IConfirmationCallback;
}

export interface IConfirmationClear extends Action {
    type: T_CONFIRMATION_CLEAR;
}

export type ConfirmationAction =
    | IConfirmationSet
    | IConfirmationClear;

// AutoLogout
export interface IAutoLogoutSet extends Action {
    type: T_AUTOLOGOUT_SET;
}

export interface IAutoLogoutClear extends Action {
    type: T_AUTOLOGOUT_CLEAR;
}

export type AutoLogoutAction =
    | IAutoLogoutSet
    | IAutoLogoutClear;

// Fetch
export interface IFetchStart extends Action {
    type: T_FETCH_START;
}

export interface IFetchEnd extends Action {
    type: T_FETCH_END;
}

export type FetchAction =
    | IFetchStart
    | IFetchEnd;

// Session
export interface ISetSessionState extends Action {
    type: T_SESSION_SET_STATE;
    sessionState: ISessionState;
}

export interface ISetNewInstantMessages extends Action {
    type: T_NEW_INSTANT_MESSAGES_SET;
    newInstantMessages: IInstantMessageListItem[];
}

export type SessionAction =
    | ISetSessionState
    | ISetNewInstantMessages;
